'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getRedirectResult, type ApplicationVerifier, type User } from 'firebase/auth';
import type { ConfirmationResult } from '@/core/services';
import { LoginPorSmsUseCase } from '../../domain/usecases/login-por-sms';
import { ReenviarCodigoUseCase } from '../../domain/usecases/reenviar-codigo';
import { VerificarCodigoOtpUseCase } from '../../domain/usecases/verificar-codigo-otp';
import { EnviarOtpVincularTelefoneUseCase } from '../../domain/usecases/enviar-otp-vincular-telefone';
import { ReenviarOtpVincularTelefoneUseCase } from '../../domain/usecases/reenviar-otp-vincular-telefone';
import { VincularTelefoneAContaUseCase } from '../../domain/usecases/vincular-telefone-a-conta';
import { LoginComGoogleUseCase } from '../../domain/usecases/login-com-google';
import { LoginComMetaUseCase } from '../../domain/usecases/login-com-meta';
import { LogoutUseCase } from '../../domain/usecases/logout';
import { ObservarEstadoAuthUseCase } from '../../domain/usecases/observar-estado-auth';
import { firebaseAuthErrorToUserMessage, firebaseAuthService } from '@/core/services';
import { AuthRepositoryImpl } from '../../data/repositories/auth-repository-impl';
import { AuthLocalForageDataSource } from '../../data/datasources/local';

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  authReady: boolean;
  confirmationResult: ConfirmationResult | null;
}

export interface AuthEvents {
  loginWithGoogle: () => Promise<void>;
  loginWithMeta: () => Promise<void>;
  sendOtp: (phoneNumber: string, appVerifier: ApplicationVerifier) => Promise<boolean>;
  resendOtp: (phoneNumber: string, appVerifier: ApplicationVerifier) => Promise<boolean>;
  /** SMS para vincular telefone ao usuário já logado (onboarding pós–rede social). */
  sendOtpForPhoneLink: (phoneNumber: string, appVerifier: ApplicationVerifier) => Promise<boolean>;
  resendOtpForPhoneLink: (phoneNumber: string, appVerifier: ApplicationVerifier) => Promise<boolean>;
  /** Confirma o OTP e vincula o telefone ao usuário atual (sem trocar o currentUser). */
  linkPhoneToAccount: (code: string) => Promise<boolean>;
  /** `true` se o código foi aceito. */
  verifyOtp: (code: string) => Promise<boolean>;
  logout: () => Promise<void>;
  clearError: () => void;
}

export interface AuthContextValue {
  state: AuthState;
  events: AuthEvents;
}

const initialState: AuthState = {
  user: null,
  loading: false,
  error: null,
  authReady: false,
  confirmationResult: null,
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(initialState);

  const loginPorSms = useMemo(() => new LoginPorSmsUseCase(), []);
  const reenviarCodigo = useMemo(() => new ReenviarCodigoUseCase(), []);
  const verificarCodigoOtp = useMemo(() => new VerificarCodigoOtpUseCase(), []);
  const enviarOtpVincularTelefone = useMemo(() => new EnviarOtpVincularTelefoneUseCase(), []);
  const reenviarOtpVincularTelefone = useMemo(() => new ReenviarOtpVincularTelefoneUseCase(), []);
  const vincularTelefoneAConta = useMemo(() => new VincularTelefoneAContaUseCase(), []);
  const loginComGoogle = useMemo(() => new LoginComGoogleUseCase(), []);
  const loginComMeta = useMemo(() => new LoginComMetaUseCase(), []);
  const authLocalDataSource = useMemo(() => new AuthLocalForageDataSource(), []);
  const authRepository = useMemo(
    () => new AuthRepositoryImpl(authLocalDataSource),
    [authLocalDataSource]
  );
  const logoutUseCase = useMemo(() => new LogoutUseCase(authRepository), [authRepository]);
  const observarEstadoAuth = useMemo(() => new ObservarEstadoAuthUseCase(), []);

  useEffect(() => {
    const auth = firebaseAuthService.getAuth();
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;

    void (async () => {
      try {
        // Obrigatório após `signInWithRedirect`: sem isso o OAuth pode não concluir e `state.user` fica null.
        await getRedirectResult(auth);
      } catch (e) {
        if (!cancelled) {
          setState((s) => ({
            ...s,
            error: firebaseAuthErrorToUserMessage(e),
          }));
        }
      }
      if (cancelled) return;
      // Evita corrida pós-retorno externo (ex.: Stripe): sem isso `onAuthStateChanged` pode emitir
      // `null` antes da persistência IndexedDB terminar e telas como verify mandam o user ao /login.
      await auth.authStateReady();
      if (cancelled) return;
      unsubscribe = observarEstadoAuth.execute((user) => {
        setState((s) => ({ ...s, user, authReady: true }));
      });
    })();

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  }, [observarEstadoAuth]);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const loginWithGoogle = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await loginComGoogle.execute();
    } catch (e) {
      setState((s) => ({
        ...s,
        error: firebaseAuthErrorToUserMessage(e),
      }));
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [loginComGoogle]);

  const loginWithMeta = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await loginComMeta.execute();
    } catch (e) {
      setState((s) => ({
        ...s,
        error: firebaseAuthErrorToUserMessage(e),
      }));
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [loginComMeta]);

  const sendOtp = useCallback(
    async (phoneNumber: string, appVerifier: ApplicationVerifier): Promise<boolean> => {
      setState((s) => ({ ...s, loading: true, error: null, confirmationResult: null }));
      try {
        const result = await loginPorSms.execute(phoneNumber, appVerifier);
        setState((s) => ({ ...s, confirmationResult: result }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [loginPorSms]
  );

  const resendOtp = useCallback(
    async (phoneNumber: string, appVerifier: ApplicationVerifier): Promise<boolean> => {
      setState((s) => ({ ...s, loading: true, error: null, confirmationResult: null }));
      try {
        const result = await reenviarCodigo.execute(phoneNumber, appVerifier);
        setState((s) => ({ ...s, confirmationResult: result }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [reenviarCodigo]
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<boolean> => {
      const { confirmationResult } = state;
      if (!confirmationResult) {
        setState((s) => ({ ...s, error: 'Envie o código por SMS primeiro.' }));
        return false;
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await verificarCodigoOtp.execute(confirmationResult, code);
        setState((s) => ({ ...s, confirmationResult: null }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [verificarCodigoOtp, state.confirmationResult]
  );

  const sendOtpForPhoneLink = useCallback(
    async (phoneNumber: string, appVerifier: ApplicationVerifier): Promise<boolean> => {
      setState((s) => ({ ...s, loading: true, error: null, confirmationResult: null }));
      try {
        const result = await enviarOtpVincularTelefone.execute(phoneNumber, appVerifier);
        setState((s) => ({ ...s, confirmationResult: result }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [enviarOtpVincularTelefone]
  );

  const resendOtpForPhoneLink = useCallback(
    async (phoneNumber: string, appVerifier: ApplicationVerifier): Promise<boolean> => {
      setState((s) => ({ ...s, loading: true, error: null, confirmationResult: null }));
      try {
        const result = await reenviarOtpVincularTelefone.execute(phoneNumber, appVerifier);
        setState((s) => ({ ...s, confirmationResult: result }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [reenviarOtpVincularTelefone]
  );

  const linkPhoneToAccount = useCallback(
    async (code: string): Promise<boolean> => {
      const { confirmationResult } = state;
      if (!confirmationResult) {
        setState((s) => ({ ...s, error: 'Envie o código por SMS primeiro.' }));
        return false;
      }

      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await vincularTelefoneAConta.execute({
          verificationId: confirmationResult.verificationId,
          code,
        });
        setState((s) => ({ ...s, confirmationResult: null }));
        return true;
      } catch (e) {
        setState((s) => ({
          ...s,
          error: firebaseAuthErrorToUserMessage(e),
        }));
        return false;
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [state.confirmationResult, vincularTelefoneAConta]
  );

  const logout = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      await logoutUseCase.execute();
      setState((s) => ({ ...s, user: null }));
    } catch (e) {
      setState((s) => ({
        ...s,
        error: firebaseAuthErrorToUserMessage(e),
      }));
    } finally {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [logoutUseCase]);

  const events: AuthEvents = useMemo(
    () => ({
      loginWithGoogle,
      loginWithMeta,
      sendOtp,
      resendOtp,
      sendOtpForPhoneLink,
      resendOtpForPhoneLink,
      linkPhoneToAccount,
      verifyOtp,
      logout,
      clearError,
    }),
    [
      loginWithGoogle,
      loginWithMeta,
      sendOtp,
      resendOtp,
      sendOtpForPhoneLink,
      resendOtpForPhoneLink,
      linkPhoneToAccount,
      verifyOtp,
      logout,
      clearError,
    ]
  );

  const value: AuthContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
